import {useMemo, useState} from "react";

import type {HmoSchemaBootstrapEntry, HmoSchemaBootstrapResult} from "@/api/hmoWikibaseSchema";
import {schemaEntryLocalId} from "@/api/hmoSchemaVerify";
import {AiVerdictPill} from "@/components/extraction/AiVerdictPill";
import {SchemaEntryDetailDrawer} from "@/components/hmo/SchemaEntryDetailDrawer";
import {CuratorTableScroll} from "@/components/CuratorTableScroll";
import type {AiVerdict} from "@/api/extractionApprovals";
import {GlassPill} from "@/components/glass";


type StatusFilter = "all" | "created" | "would_create" | "skipped" | "failed";


export interface SchemaBootstrapDetailsProps {
  result: HmoSchemaBootstrapResult;
  defaultExpanded?: boolean;
  verdicts?: Record<string, AiVerdict>;
  /** When present, rows can be opened in a detail drawer that cross-
   *  references this run's actual RDF graph for real usage data. */
  runId?: string;
  wikibaseBaseUrl?: string;
}


// "skipped" is the backend/filter value (idempotency marker — this
// ontology URI already has a live Wikibase id from a previous
// bootstrap) but reads as "nothing happened" to a curator. Every
// status label shown in the UI uses this friendlier wording instead.
const STATUS_LABELS: Record<string, string> = {
  created: "created",
  would_create: "would create",
  skipped: "mapped",
  failed: "failed",
};

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

function downloadJson(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], {type: "application/json"});
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}


function countByStatus(entries: HmoSchemaBootstrapEntry[]): Record<string, number> {
  const counts: Record<string, number> = {
    created: 0,
    would_create: 0,
    skipped: 0,
    failed: 0,
  };
  for (const e of entries) {
    if (e.status in counts) counts[e.status] += 1;
  }
  return counts;
}


export function SchemaBootstrapDetails({
  result,
  defaultExpanded = true,
  verdicts = {},
  runId,
  wikibaseBaseUrl,
}: SchemaBootstrapDetailsProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [query, setQuery] = useState("");
  const [selectedEntry, setSelectedEntry] = useState<HmoSchemaBootstrapEntry | null>(null);

  const counts = useMemo(() => countByStatus(result.entries), [result.entries]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return result.entries.filter((entry) => {
      if (statusFilter !== "all" && entry.status !== statusFilter) return false;
      if (!q) return true;
      return (
        entry.label.toLowerCase().includes(q)
        || entry.ontology_uri.toLowerCase().includes(q)
        || (entry.wikibase_id ?? "").toLowerCase().includes(q)
      );
    });
  }, [result.entries, statusFilter, query]);

  const headline = result.dry_run ? "Would create" : "Created";
  const headlineCount = result.dry_run ? result.would_create : result.created;

  function exportJson() {
    const payload = {
      generated_at: new Date().toISOString(),
      dry_run: result.dry_run,
      created: result.created,
      skipped: result.skipped,
      failed: result.failed,
      would_create: result.would_create,
      entries: result.entries.map((entry) => ({
        ...entry,
        ai_verdict: verdicts[schemaEntryLocalId(entry)] ?? verdicts[entry.ontology_uri] ?? null,
      })),
    };
    downloadJson(`hmo-wikibase-schema-${new Date().toISOString().slice(0, 10)}.json`, payload);
  }

  return (
    <div className="border-t border-white/5 pt-3 space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm">
          <span className="muted">{headline}:</span>{" "}
          <b className="text-biu-sky">{headlineCount}</b>
          {" · "}
          <span className="muted">mapped {result.skipped}</span>
          {result.failed > 0 && (
            <>
              {" · "}
              <span className="text-danger">failed {result.failed}</span>
            </>
          )}
        </p>
        <div className="flex items-center gap-2">
          <button onClick={exportJson} className="button-ghost text-xs">
            Export JSON
          </button>
          <button onClick={() => setExpanded((v) => !v)} className="button-ghost text-xs">
            {expanded ? "Hide details" : "Show details"}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <SummaryChip
          label="would create"
          count={counts.would_create}
          active={statusFilter === "would_create"}
          onClick={() => setStatusFilter((f) => (f === "would_create" ? "all" : "would_create"))}
          tone="text-warn"
        />
        <SummaryChip
          label="created"
          count={counts.created}
          active={statusFilter === "created"}
          onClick={() => setStatusFilter((f) => (f === "created" ? "all" : "created"))}
          tone="text-biu-sky"
        />
        <SummaryChip
          label="mapped"
          count={counts.skipped}
          active={statusFilter === "skipped"}
          onClick={() => setStatusFilter((f) => (f === "skipped" ? "all" : "skipped"))}
          tone="muted"
        />
        <SummaryChip
          label="failed"
          count={counts.failed}
          active={statusFilter === "failed"}
          onClick={() => setStatusFilter((f) => (f === "failed" ? "all" : "failed"))}
          tone="text-danger"
        />
      </div>

      {expanded && (
        <>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by label, URI, or Wikibase id…"
            className="input-glass w-full text-sm"
          />
          <CuratorTableScroll>
            <table className="w-full text-sm">
              <thead className="bg-white/3 text-xs uppercase muted tracking-wider sticky top-0 z-10 table-head">
                <tr>
                  <th className="text-left px-3 py-2">Kind</th>
                  <th className="text-left px-3 py-2">Label</th>
                  <th className="text-left px-3 py-2">Ontology URI</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Wikibase id</th>
                  <th className="text-left px-3 py-2">AI verdict</th>
                  <th className="text-left px-3 py-2">Message</th>
                  <th className="text-left px-3 py-2">Deep dive</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((entry) => {
                  const localId = schemaEntryLocalId(entry);
                  const verdict = verdicts[localId] ?? verdicts[entry.ontology_uri] ?? null;
                  return (
                    <tr
                      key={entry.ontology_uri}
                      className="border-t border-white/5 align-top cursor-pointer hover:bg-white/3"
                      onClick={() => setSelectedEntry(entry)}
                    >
                      <td className="px-3 py-2 text-xs muted">{entry.entity_kind}</td>
                      <td className="px-3 py-2 text-xs">{entry.label}</td>
                      <td className="px-3 py-2 text-xs font-mono max-w-[14rem] truncate" title={entry.ontology_uri}>
                        {entry.ontology_uri}
                      </td>
                      <td className="px-3 py-2">
                        <EntryStatusPill status={entry.status} />
                      </td>
                      <td className="px-3 py-2 text-xs font-mono">{entry.wikibase_id ?? "—"}</td>
                      <td className="px-3 py-2">
                        <AiVerdictPill verdict={verdict} size="sm" />
                      </td>
                      <td className="px-3 py-2 text-xs muted max-w-[12rem]">{entry.message || "—"}</td>
                      <td className="px-3 py-2 text-xs text-biu-sky">Open →</td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-6 text-center muted text-sm">
                      No entries match the current filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CuratorTableScroll>
          <p className="text-xs muted">
            Showing {rows.length} of {result.entries.length} entries · click a row for the full
            detail (description, Wikibase link, AI reasoning, and real usage in the RDF graph)
            {counts.skipped > 0 && (
              <>
                {" · "}
                <span title="Nothing was skipped over — every one of these ontology URIs was checked against the mapping table and already has a live Wikibase Item/Property id from a previous bootstrap run, so re-running creates nothing new for them.">
                  Mapped = already live on Wikibase, re-checked every run
                </span>
              </>
            )}
          </p>
        </>
      )}
      <SchemaEntryDetailDrawer
        entry={selectedEntry}
        runId={runId}
        wikibaseBaseUrl={wikibaseBaseUrl}
        verdict={
          selectedEntry
            ? verdicts[schemaEntryLocalId(selectedEntry)] ?? verdicts[selectedEntry.ontology_uri] ?? null
            : null
        }
        onClose={() => setSelectedEntry(null)}
      />
    </div>
  );
}


function SummaryChip({
  label,
  count,
  active,
  onClick,
  tone,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  tone: string;
}) {
  if (count === 0) return null;
  return (
    <button type="button" onClick={onClick}>
      <GlassPill
        className={`px-2 py-0.5 text-[10px] kicker ${tone} ${active ? "ring-1 ring-biu-sky/60" : ""}`}
      >
        {label} {count}
      </GlassPill>
    </button>
  );
}


function EntryStatusPill({status}: {status: string}) {
  const tone =
    status === "created"
      ? "text-biu-sky"
      : status === "would_create"
        ? "text-warn"
        : status === "skipped"
          ? "muted"
          : "text-danger";
  return <GlassPill className={`px-2 py-0.5 text-[10px] kicker ${tone}`}>{statusLabel(status)}</GlassPill>;
}
